import { AutomationStep, StepLevelRetryConfig, StepReasoningEffort } from '@/types/automationSteps.ts';
import { Accordion, Stack } from '@mantine/core';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StyledMultiSelect from '@/components/StyledInputFields/StyledMultiSelect.tsx';
import StyledNumberInput from '@/components/StyledInputFields/StyledNumberInput.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import useRetrieveLlmModelOptions from '@/hooks/api/llmModels/useRetrieveLlmModelOptions.ts';
import { useMemo, useState } from 'react';
import {
  AUTOMATION_DEFAULT_MODEL_OPTION_VALUE,
  buildModelSelectOptions,
} from '@/utils/llmModelSelectOptions.ts';
import {
  buildStepReasoningOptions,
} from '@/utils/reasoningOptions.ts';
import { StepType } from '@/types/automationFields.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { getPreviousSiblingStepOptions } from '@/pages/Editor/components/StepSidebar/StepForms/controlDependencyStepOptions.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import { supportsStepLevelRetry, supportsStepLevelRetryGuidance } from '@/utils/stepRetryCapabilities.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';

type props = {
  step: AutomationStep;
};

type AdvancedConfigFields = {
  is_disabled: boolean;
  wait_for_step_ids: string[];
  reasoning: StepReasoningEffort;
  primary_model_override: string | null;
  secondary_model_override: string | null;
  semantic_recovery_primary_model?: string | null;
  retry_config: StepLevelRetryConfig | null;
};

const ADVANCED_CONFIG_VALUE = 'advanced-config';
const DEFAULT_STEP_LEVEL_RETRY_CONFIG: StepLevelRetryConfig = {
  enabled: false,
  max_retry_count: 1,
  use_failure_feedback: true,
};

const StepAdvancedConfigFields = ({ step }: props) => {
  const { fields, onChangeNoDelay } = useUpdateStepFields<AdvancedConfigFields>({ step });
  const [advancedConfigValue, setAdvancedConfigValue] = useState<string | null>(null);
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const { target } = useActiveCompositionTarget();
  const invalidStepWait = useStepSpecificError(step, StepErrorType.InvalidStepWait);

  const advancedConfigOpened = advancedConfigValue === ADVANCED_CONFIG_VALUE;

  const {
    data: modelOptions,
    isLoading: isLoadingModelOptions,
    isError: isModelOptionsError,
  } = useRetrieveLlmModelOptions({ enabled: advancedConfigOpened });

  const modelById = useMemo(() => {
    return new Map(modelOptions?.models.map((model) => [model.model, model]) ?? []);
  }, [modelOptions?.models]);

  const primaryModelOptions = useMemo(() => {
    return buildModelSelectOptions({
      modelIds: modelOptions?.step_primary_models ?? [],
      modelById,
      currentModelId: fields.primary_model_override,
      includeAutomationDefault: true,
    });
  }, [fields.primary_model_override, modelById, modelOptions?.step_primary_models]);

  const secondaryModelOptions = useMemo(() => {
    return buildModelSelectOptions({
      modelIds: modelOptions?.step_secondary_models ?? [],
      modelById,
      currentModelId: fields.secondary_model_override,
      includeAutomationDefault: true,
    });
  }, [fields.secondary_model_override, modelById, modelOptions?.step_secondary_models]);
  const semanticRecoveryModelOptions = useMemo(() => {
    const effectivePrimaryModel =
      fields.primary_model_override ?? latestVersion?.model_settings.primary_model;
    const eligibleRecoveryModels = effectivePrimaryModel
      ? (modelOptions?.semantic_recovery_primary_models_by_primary[effectivePrimaryModel] ?? [])
      : [];

    return [
      { value: AUTOMATION_DEFAULT_MODEL_OPTION_VALUE, label: 'Disabled' },
      ...buildModelSelectOptions({
        modelIds: eligibleRecoveryModels,
        modelById,
        currentModelId: fields.semantic_recovery_primary_model ?? null,
      }),
    ];
  }, [
    fields.primary_model_override,
    fields.semantic_recovery_primary_model,
    latestVersion?.model_settings.primary_model,
    modelById,
    modelOptions?.semantic_recovery_primary_models_by_primary,
  ]);

  const reasoningOptions = buildStepReasoningOptions(fields.reasoning);

  const modelOverrideDisabled = isLoadingModelOptions || isModelOptionsError;
  const modelOverrideError = isModelOptionsError ? 'Unable to load model options' : undefined;
  const stepScope = useMemo(
    () => target.scope_kind === 'local_subflow'
      ? latestVersion?.local_subflows?.find((subflow) => subflow.subflow_id === target.subflow_id)?.steps
      : latestVersion?.steps,
    [latestVersion?.local_subflows, latestVersion?.steps, target],
  );
  const waitForStepOptions = useMemo(
    () => getPreviousSiblingStepOptions((stepScope ?? []) as AutomationStep[], step.step_id),
    [stepScope, step.step_id],
  );

  const getModelOverrideSelectValue = (value: string | null) => {
    return value || AUTOMATION_DEFAULT_MODEL_OPTION_VALUE;
  };

  const getModelOverrideUpdateValue = (value: string | null) => {
    return value === AUTOMATION_DEFAULT_MODEL_OPTION_VALUE ? null : value;
  };
  const retryConfig = fields.retry_config ?? DEFAULT_STEP_LEVEL_RETRY_CONFIG;
  const stepLevelRetrySupported = target.scope_kind !== 'local_subflow' && supportsStepLevelRetry(step.step_type);
  const stepLevelRetryGuidanceSupported = supportsStepLevelRetryGuidance(step.step_type);

  const updateRetryConfig = (updates: Partial<StepLevelRetryConfig>) => {
    onChangeNoDelay({ retry_config: { ...retryConfig, ...updates } });
  };

  return (
    <Stack w={'100%'}>
      {step.step_type !== StepType.Trigger && (
        <StyledSwitch
          label="Step enabled"
          checked={!fields.is_disabled}
          onChange={(event) => onChangeNoDelay({ is_disabled: !event.currentTarget.checked })}
        />
      )}

      <Accordion value={advancedConfigValue} onChange={setAdvancedConfigValue} variant="contained">
        <Accordion.Item value={ADVANCED_CONFIG_VALUE}>
          <Accordion.Control>Advanced config</Accordion.Control>
          <Accordion.Panel>
            <Stack gap={'sm'}>
              <StyledSelect
                label="Reasoning Level"
                data={reasoningOptions}
                value={fields.reasoning}
                onChange={(value) => onChangeNoDelay({ reasoning: value as StepReasoningEffort })}
                required={true}
                allowDeselect={false}
                clearable={false}
              />
              {step.step_type !== StepType.Trigger && (
                <StyledMultiSelect
                  label="Wait for steps"
                  placeholder="Select earlier steps"
                  data={waitForStepOptions}
                  value={fields.wait_for_step_ids ?? []}
                  onChange={(value) => onChangeNoDelay({ wait_for_step_ids: value })}
                  error={invalidStepWait}
                  searchable={true}
                  clearable={true}
                />
              )}
              <StyledSelect
                label="Primary model"
                data={primaryModelOptions}
                value={getModelOverrideSelectValue(fields.primary_model_override)}
                onChange={(value) =>
                  onChangeNoDelay({ primary_model_override: getModelOverrideUpdateValue(value) })
                }
                disabled={modelOverrideDisabled}
                error={modelOverrideError}
                allowDeselect={false}
                clearable={false}
              />
              {(step.step_type === StepType.TransformData || step.step_type === StepType.ExtractData) && (
                <StyledSelect
                  label="Semantic recovery model"
                  description="Used only after an in-step semantic rejection or verifier-requested repair. It must be stronger than the effective primary model."
                  data={semanticRecoveryModelOptions}
                  value={getModelOverrideSelectValue(fields.semantic_recovery_primary_model ?? null)}
                  onChange={(value) =>
                    onChangeNoDelay({
                      semantic_recovery_primary_model: getModelOverrideUpdateValue(value),
                    })
                  }
                  disabled={modelOverrideDisabled}
                  error={modelOverrideError}
                  allowDeselect={false}
                  clearable={false}
                />
              )}
              <StyledSelect
                label="Secondary model"
                data={secondaryModelOptions}
                value={getModelOverrideSelectValue(fields.secondary_model_override)}
                onChange={(value) =>
                  onChangeNoDelay({ secondary_model_override: getModelOverrideUpdateValue(value) })
                }
                disabled={modelOverrideDisabled}
                error={modelOverrideError}
                allowDeselect={false}
                clearable={false}
              />
              {stepLevelRetrySupported && (
                <>
                  <StyledSwitch
                    label="Retry this step if it fails"
                    checked={retryConfig.enabled}
                    onChange={(event) =>
                      updateRetryConfig({
                        enabled: event.currentTarget.checked,
                        use_failure_feedback: stepLevelRetryGuidanceSupported
                          ? retryConfig.use_failure_feedback
                          : false,
                      })
                    }
                  />
                  {retryConfig.enabled && (
                    <>
                      <StyledNumberInput
                        label="Retry count"
                        min={1}
                        max={5}
                        clampBehavior="strict"
                        allowDecimal={false}
                        value={retryConfig.max_retry_count}
                        onChange={(value) =>
                          updateRetryConfig({ max_retry_count: typeof value === 'number' ? value : 1 })
                        }
                      />
                      {stepLevelRetryGuidanceSupported && (
                        <StyledSwitch
                          label="Use failure as retry guidance"
                          checked={retryConfig.use_failure_feedback}
                          onChange={(event) =>
                            updateRetryConfig({ use_failure_feedback: event.currentTarget.checked })
                          }
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
};

export default StepAdvancedConfigFields;
